import { RouteComponentProps } from '@reach/router'
import { FolderUW_s } from "components/folder/underwriting-workbook/folder-uw-styles"
import DataTable_s from 'components/table/data-table-styles'
// Data
import { alphanum } from "components/table/lib/sort"
import folderService from 'data/folder'
import { local } from 'data/lib/local-json'
import notificationService from "data/notification"
import propertyService from "data/property"
import socketService, * as eventType from "data/socket"
import underwritingService from "data/underwriting"
// Icons
import { CommercialIcon, HospitalityIcon, MFRentIcon, MixedUseIcon } from 'images/icons/sidebar'
import _ from 'lodash'
import Moment from 'moment'
import { Column } from 'primereact/column'
// Components
import { DataTable } from 'primereact/datatable'
import React, { useEffect, useState } from 'react'
import Isvg from 'react-inlinesvg'
// Styles
import { ButtonWithPress_s, Label_s } from "styles/app-styles"
import { FormError_s } from "styles/form-styles"
import { InputNumber_s, InputText_s } from "styles/input-styles"
import { DateText_s } from "styles/report-styles"

const FolderUW = (props: RouteComponentProps) => {
  const [valuationDate, setValuationDate]   = useState<string|undefined>()
  const [description, setDescription]       = useState<string>(getPlaceholderDescription())
  const [term, setTerm]                     = useState<number|null>(null)
  const [properties, setProperties]         = useState<object[]>([])
  const [data, setData]                     = useState<object[]>([])
  const [selected, setSelected]             = useState<object[]>([])
  const [valid, setValid]                   = useState<boolean>(true)
  const [issues, setIssues]                 = useState<string[]>([])

  useEffect(() => {
    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.value.length > 0){
        setProperties(data.value)
        setData([...data.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))))
      }
    })

    const currentFolderId = _.last(local('folderPath'))
    folderService.fetchFolder(currentFolderId).then(folder => {
      if(folder){
        setValuationDate(getDefaultStartDate(folder))
        setTerm(getDefaultReportTerm(folder))
      }
    })
    propertyService.fetchByFolderId(currentFolderId, false)

    const socketSubscription = socketService.stream().subscribe(socket => {
      switch(true) {
        case socket.eventType === eventType.DATA_EXPORT_READY:
          notificationService.notify({
            type: "info",
            message: "Underwriting Workbook is ready. Please check the Dashboard.",
            dismiss: 5000
          })
          break
      }
    })

    return function cleanup() {
      propertySubscription.unsubscribe()
      socketSubscription.unsubscribe()
    }
  }, []);

  useEffect(() => {
    highlightAndValidate()
  }, [valuationDate, term, selected]);

  function getDefaultStartDate(folder:object):string|undefined {
    // BUSINESS LOGIC: If the Folder Report Begin is null default the Valuation Date
    // to latest Report Begin Date of the properties in that folder
    if (folder['reportbegindate']) return folder['reportbegindate']
    else {
      let latestReportBegin;
      for (let i of properties){
        let nextDate = i['reportbegindate'];
        if (!latestReportBegin || Moment(nextDate).isAfter(latestReportBegin)){
          latestReportBegin = nextDate;
        }
      }
      return latestReportBegin;
    }
  }

  function getDefaultReportTerm(folder:object):number {
    // BUSINESS LOGIC:
    // Default to folder term if exists,
    // Else the lesser of:
      // Rounded down yrs between valuation date and min property analysis end date of the folder's properties
        // Property Analysis end date is determined by the property analysis start date + term
     // 10 Yrs
    // And if term is < 1 yr, default to 10 yrs
    if (folder['term']) {
      return folder['term']
    } else {
      let minPropertyAnalysisEndDate;
      properties.map(p => {
        let endDate = Moment(p['analysisbegindate']).add(p['term'], 'years')
        if (!minPropertyAnalysisEndDate || endDate.isBefore(minPropertyAnalysisEndDate)) minPropertyAnalysisEndDate = endDate.calendar()
      });
      let yearsBetween = Math.floor(Moment(minPropertyAnalysisEndDate).diff(Moment(getDefaultStartDate(folder)), 'months') / 12);
      return yearsBetween < 10 && yearsBetween> 0 ? yearsBetween : 10
    }
  }

  function getPlaceholderDescription():string {
    const created  = Moment().utc().local().format('MM/DD/YYYY h:mm:ss A');
    const zoneName = Moment.tz.guess();
    const timezone = Moment.tz(zoneName).zoneAbbr();
    return `Folder Underwriting on: ${created} ${timezone}`;
  }

  function getScenario(rowData:object):string {
    const defaultScenario = _.find(rowData['valuationdetail'], v => v.defaultScenario === true);
    const scenarioLabel = !defaultScenario ? 'N/A' : defaultScenario.valuationDescription ? `${defaultScenario.valuationPeriod} - ${defaultScenario.valuationDescription}` : defaultScenario.valuationPeriod
    return scenarioLabel
  }

  function highlightAndValidate() {
    let date = valuationDate;
    let reportTerm = term;
    let valuationEnd = Moment(valuationDate).add(reportTerm, 'years');
    let invalid = true;
    let issues:string[] = [];
    // Validation BUSINESS LOGIC: Disable submit button if not passed //
    // Valuation Date should be > every selected property's analysis begin date
    // Valuation date + term should be < every selected property's analysis end (property analysis begin date + term)
    if (_.find(selected, p => Moment(date).isBefore(Moment(p.analysisbegindate)))){
      issues.push('Valuation Date must be after all selected property analysis begin dates');
      invalid = false;
    }
    if (_.find(selected, p => valuationEnd.isAfter(Moment(p.analysisbegindate).add(p.term, 'years')))){
      issues.push('Not all selected properties have sufficient term for the specified valuation date and term');
      invalid = false;
    }
    if (_.find(selected, p => p.calcdetail?.status !== 'Calculated')){
      issues.push('Properties must be calculated to be included')
      invalid = false;
    }
    if (_.find(selected, p => p.type === 'Multifamily')){
      issues.push('Multifamily properties not currently supported')
      invalid = false;
    }
    if (_.find(selected, p => p.type === 'Hospitality')){
      issues.push('Hospitality properties not currently supported')
      invalid = false;
    }
    if (reportTerm && reportTerm < 3) {
      issues.push('Term must be at least 3');
      invalid = false;
    }
    if (_.find(selected, p => p.valuationType && p.valuationType === 'Direct Cap')){
      issues.push('Underwriting workbook does not support the Direct Cap Valuation Type')
      invalid = false;
    }
    setValid(invalid)
    setIssues(issues)
  }

  type DataTableComponent = {props:{value: object[]}}

  function sortFn (this: DataTableComponent, event) {
    let data = [...this.props.value]
    const { field, order } = event
    if (order === 1) data.sort((a, b) => alphanum(_.get(a, field), _.get(b, field)))
    else data.sort((a, b) => alphanum(_.get(b, field), _.get(a, field)))
    return data
  }

  const submit = () => {
    const selectedProps:number[] = selected.map(p => p['propertyid'])
    underwritingService.quickReport(null, underwritingService.toISOdate(valuationDate), term, null, selectedProps);
  }

  const rowClassName = (rowData) => {
    return {'error-row' : (rowData?.type === 'Multifamily' || !rowData.calcdetail?.status?.startsWith('Calculated'))};
  }

  const commercialIcon = <Isvg key={CommercialIcon} src={CommercialIcon} cacheGetRequests style={{height: '16px', margin: '3px 0 0 9px'}} />;
  const hospitalityIcon = <Isvg key={HospitalityIcon} src={HospitalityIcon} cacheGetRequests style={{height: '16px', margin: '5px 0 0 11px'}} />;
  const mfIcon = <Isvg key={MFRentIcon} src={MFRentIcon} cacheGetRequests style={{height: '20px', margin: '2px 0 0 12px'}} />;
  const mixedUseIcon = <Isvg key={MixedUseIcon} src={MixedUseIcon} cacheGetRequests style={{height: '16px', margin: '3px 0 0 9px'}} />;

  return (
    <FolderUW_s>
      <div>
        <div>
          <div>
            <Label_s>Valuation Date</Label_s>
            <DateText_s withoutDays value={valuationDate || ''} onChange={setValuationDate} onBlur={setValuationDate} persist={setValuationDate} />
          </div>
          <div>
            <Label_s>Term</Label_s>
            <InputNumber_s value={term} onChange={(e) => setTerm(e.value)} maxFractionDigits={0} />
          </div>
          <div>
            <Label_s>Description</Label_s>
            <InputText_s
              className="automated"
              value={description}
              onChange={(e) => setDescription((e.target as HTMLInputElement).value)}
              style={{width: '350px'}}
            />
          </div>
          <div>
            <ButtonWithPress_s
              disabled={selected.length === 0 || !term || !valuationDate || !valid || _.find(selected, p => p.calcdetail?.status !== 'Calculated') !== undefined || _.find(selected, p => p.type === 'Multifamily') !== undefined || _.find(selected, p => p.type === 'Hospitality') !== undefined}
              onClick={submit}
            >
              Submit
            </ButtonWithPress_s>
          </div>
        </div>
        <div>{ issues.map(issue => { return <FormError_s>{issue}</FormError_s>}) }</div>
      </div>
      <div>
        <label>Selected Properties</label>
        <DataTable_s>
          <DataTable
            value={data}
            paginator={true}
            rows={20}
            rowsPerPageOptions={[10, 20, 50, 100, 500]}
            rowClassName={rowClassName}
            emptyMessage="No records found"
            selection={selected}
            onSelectionChange={e => { setSelected(e.value); }}
            footer={
              <Label_s style={{ float: 'left', position: 'absolute', zIndex: '2' }}>
                {selected.length} selected properties
              </Label_s>
            }>
            <Column selectionMode="multiple" />
            <Column
              header="Type"
              field="type"
              sortable={true}
              body={(rowData) => rowData.type === 'Multifamily' ? mfIcon : rowData.type === 'Hospitality' ? hospitalityIcon : rowData.type === 'Mixed-Use' ? mixedUseIcon : commercialIcon}
              style={{width: '70px'}}
            />
            <Column
              field="code"
              header="Property"
              filter={true}
              sortable={true}
              style={{width: '300px'}}
              body={(rowData, props) => `${rowData.code} ${rowData.name ? `- ${rowData.name}` : ''}`}
            />
            <Column
              field="analysisbegindate"
              header="Analysis Begin"
              body={(rowData, props) => Moment(rowData.analysisbegindate).format('MM/DD/YYYY')}
              filter={true}
              sortable={true}
              sortFunction={sortFn}
              style={{width: '130px'}}
            />
            <Column
              field="reportbegindate"
              header="Report Begin"
              body={(rowData, props) => Moment(rowData.reportbegindate).format('MM/DD/YYYY')}
              filter={true}
              sortable={true}
              sortFunction={sortFn}
              style={{width: '120px'}}
            />
            <Column field="term" header="Term" filter={true} sortable={true} style={{width: '80px'}} />
            <Column
              field="valuationdetail"
              header="Scenario"
              filter={true}
              sortable={true}
              sortFunction={sortFn}
              style={{width: '150px'}}
              body={(rowData, props) => getScenario(rowData)}
            />
            <Column
              field="calcdetail.status"
              header="Calculation Status"
              filter={true}
              sortable={true}
              sortFunction={sortFn}
              style={{width: '150px'}}
              body={(rowData, props) => rowData.calcdetail?.status || 'Uncalculated'}
            />
          </DataTable>
        </DataTable_s>
      </div>
    </FolderUW_s>
  )
}

export default FolderUW
